import React, { Fragment, useState, useEffect, useCallback } from "react";
import { Form, Button, Table, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Breadcrumbs } from "../../AbstractElements";
import { Card, CardBody, Col } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useLoader } from "../../_helper/LoaderContext";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import { returnPurchase, viewStockDetails } from "./StockService";
import { toast } from "react-toastify";
import { FaArrowLeft } from "react-icons/fa6";
import { useDisableScrollOnFocus } from "../../Hooks/useDisableScrollOnFocus";
import { useCurrency } from "../../_helper/CurrencyContext";
import { shouldShowPurchaseTaxUi } from "../PharmacyManageTax/taxConfigUtils";

const ReturnPurchase = () => {
	const { t } = useTranslation();
	const { getCurrencySymbol } = useCurrency();
	const { stkId } = useParams();
	const { loading, setLoading } = useLoader();
	const { pharmacyPOS } = usePharmacy();
	const navigate = useNavigate();
	const disableScrollProps = useDisableScrollOnFocus();

	// ============ FORM STATE ============
	const [supplierId, setSupplierId] = useState("");
	const [supplierName, setSupplierName] = useState("");
	const [items, setItems] = useState([]);
	const [note, setNote] = useState("");
	const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
	const [currency, setCurrency] = useState("INR");

	// ============ TAX SYSTEM STATE ============
	const [pharmacyStateCode] = useState(pharmacyPOS?.stateCode || "");
	const [supplierStateCode, setSupplierStateCode] = useState("");
	const [posTaxType] = useState(pharmacyPOS?.taxType || "gst");
	const [purchaseTaxType, setPurchaseTaxType] = useState("");
	const [isIGST, setIsIGST] = useState(false);
	const [showPurchaseTax, setShowPurchaseTax] = useState(false);

	// ============ TOTALS STATE ============
	const [netTotal, setNetTotal] = useState(0);
	const [returnBreakdown, setReturnBreakdown] = useState({
		grossReturnTotal: 0,
		unpaidDeduction: 0,
		refundTotal: 0,
	});
	const [purchaseSummary, setPurchaseSummary] = useState({
		netTotal: 0,
		totalPaid: 0,
		balance: 0,
		purchaseAmountHistory: [],
	});
	const [taxSummary, setTaxSummary] = useState({
		CGST: 0,
		SGST: 0,
		IGST: 0,
		totalTax: 0,
		taxableAmount: 0,
	});

	const formatDate = (dateString) => {
		if (!dateString) return "-";
		const date = new Date(dateString);
		const day = date.getDate().toString().padStart(2, "0");
		const month = date.toLocaleString(undefined, { month: "short" });
		const year = date.getFullYear();
		return `${day} ${month} ${year}`;
	};

	const isPartialPayment =
		purchaseSummary.netTotal > 0 &&
		purchaseSummary.totalPaid > 0 &&
		purchaseSummary.totalPaid < purchaseSummary.netTotal;

	// ============ FETCH PURCHASE DETAILS ============
	useEffect(() => {
		const fetchStock = async () => {
			const body = {
				clinicId: pharmacyPOS?.hospital_id,
				purchaseId: stkId,
				type: "return",
			};
			try {
				setLoading(true);
				const response = await viewStockDetails(body);
				if (response.data?.status === "success") {
					const stockData = response.data.data.purchaseDetails;
					setSupplierId(stockData.supplierId || "");
					setSupplierName(stockData.supplierName || "");
					setNote(stockData.notes || "");
					setDate(
						stockData.purchaseDate ? stockData.purchaseDate.split("T")[0] : ""
					);
					setCurrency(stockData.currencyType || "INR");
					setPurchaseTaxType(stockData.taxType || "gst");
					setSupplierStateCode(stockData.supplierStateCode || "");
					setIsIGST(stockData.isIGST || false);
					setPurchaseSummary({
						netTotal: Number(stockData.NetTotal) || 0,
						totalPaid: Number(stockData.totalPaid) || 0,
						balance:
							Number(stockData.balance) ??
							Math.max(
								0,
								(Number(stockData.NetTotal) || 0) -
								(Number(stockData.totalPaid) || 0)
							),
						purchaseAmountHistory: stockData.purchaseAmountHistory || [],
					});

					// Map stocks to items - derive per-medicine taxable/tax from unitPrice & tax %
					const mappedItems = (stockData.stocks || []).map((item) => {
						const originalTaxableAmount = item.taxableAmount ?? 0;
						const originalCGST = item.CGST ?? 0;
						const originalSGST = item.SGST ?? 0;
						const originalIGST = item.IGST ?? 0;

						const IGSTPercent = item.IGSTPercentage || 0;
						const CGSTPercent = item.CGSTPercentage || 0;
						const SGSTPercent = item.SGSTPercentage || 0;

						const totalTaxPercent =
							IGSTPercent > 0
								? IGSTPercent
								: (CGSTPercent + SGSTPercent) || item.taxPercentage || 0;

						const unitPrice = Number(item.unitPrice) || 0; // tax-inclusive per-medicine price

						let taxablePerMedicine = 0;
						let CGSTPerMedicine = 0;
						let SGSTPerMedicine = 0;
						let IGSTPerMedicine = 0;

						if (unitPrice > 0) {
							if (totalTaxPercent === 0) {
								taxablePerMedicine = unitPrice;
							} else {
								taxablePerMedicine = (unitPrice * 100) / (100 + totalTaxPercent);
							}

							const totalTaxPerMedicine = unitPrice - taxablePerMedicine;

							if (IGSTPercent > 0) {
								IGSTPerMedicine = totalTaxPerMedicine;
							} else if (totalTaxPercent > 0) {
								if (CGSTPercent > 0) {
									CGSTPerMedicine =
										totalTaxPerMedicine * (CGSTPercent / totalTaxPercent);
								}
								if (SGSTPercent > 0) {
									SGSTPerMedicine =
										totalTaxPerMedicine * (SGSTPercent / totalTaxPercent);
								}
							}
						}

						return {
							_id: item.itemId,
							stockId: item._id,

							code: item.code,
							brandName: item.brandName,
							medicineName: item.medicineName,
							manufacturer: item.manufacturerName,
							package: item.package,
							packageQuantity: item.packing,
							isOTC: item.isOTC,
							quantity: Number(item.quantity),
							itemQuantity: Number(item.totalRemainingMedicine),
							packageMRP: Number(item.MRP),
							packageRate: Number(item.rate), // tax-exclusive rate
							unitPrice, // tax-inclusive per-medicine price
							unitSalePrice: Number(item.unitPricePerMedicine),

							batchNo: item.batchNumber,
							manufacturingDate: item.manufacturingDate?.split("T")[0] || "",
							expiryDate: item.expiryDate?.split("T")[0] || "",

							taxPercentage: item.taxPercentage || totalTaxPercent || 0,

							// Per-medicine values derived from unitPrice
							taxablePerMedicine,
							CGSTPerMedicine,
							SGSTPerMedicine,
							IGSTPerMedicine,

							// Percentage values from API
							IGSTPercent,
							CGSTPercent,
							SGSTPercent,

							returnQuantity: 0,
							returnItemQuantity: 0,

							returnCGSTAmount: 0,
							returnSGSTAmount: 0,
							returnIGSTAmount: 0,
							returnTaxAmount: 0,

							originalTaxableAmount,
							originalCGST,
							originalSGST,
							originalIGST,
						};
					});
					setItems(mappedItems);
					setShowPurchaseTax(
						shouldShowPurchaseTaxUi(pharmacyPOS, {
							purchaseTax: stockData.tax,
							items: mappedItems,
						})
					);
				}
			} catch (error) {
				console.error("Error fetching stock details:", error);
				toast.warning(t("Failed to fetch purchase details"));
			} finally {
				setLoading(false);
			}
		};

		fetchStock();
	}, [stkId, pharmacyPOS?.hospital_id, setLoading]);

	// ============ UPDATE RETURN QUANTITY ============
	const updateReturnQuantity = (_id, value) => {
		const val = Math.max(0, Number(value) || 0);

		setItems(prev =>
			prev.map(item => {
				if (item._id !== _id) return item;
				if (val > item.itemQuantity) return item;

				const returnQty = val;

				// Calculate based on high-precision per-unit values from original purchase
				const returnTaxableAmount = returnQty * (item.taxablePerMedicine || 0);
				const returnCGSTAmount = returnQty * (item.CGSTPerMedicine || 0);
				const returnSGSTAmount = returnQty * (item.SGSTPerMedicine || 0);
				const returnIGSTAmount = returnQty * (item.IGSTPerMedicine || 0);
				const returnTaxAmount = returnCGSTAmount + returnSGSTAmount + returnIGSTAmount;

				return {
					...item,
					returnQuantity: returnQty,
					returnItemQuantity: returnQty, // units
					returnTaxableAmount,
					returnCGSTAmount,
					returnSGSTAmount,
					returnIGSTAmount,
					returnTaxAmount,
					returnTotalAmount: returnTaxableAmount + returnTaxAmount,
				};
			})
		);
	};

	// ============ REMOVE ITEM ============
	const removeItem = (_id) => {
		setItems((prev) => prev.filter((item) => item._id !== _id));
	};

	// ============ CALCULATE TOTALS ============
	const recalculateAll = useCallback(() => {
		let subtotal = 0, CGST = 0, SGST = 0, IGST = 0, taxableAmount = 0;

		items.forEach(item => {
			if (item.returnQuantity > 0) {
				subtotal += item.returnTotalAmount;
				taxableAmount += item.returnTaxableAmount;
				CGST += item.returnCGSTAmount;
				SGST += item.returnSGSTAmount;
				IGST += item.returnIGSTAmount;
			}
		});

		const totalTax = CGST + SGST + IGST;
		const grossReturnTotal = Number((subtotal || 0).toFixed(2));

		const purchaseNetTotal = Number(purchaseSummary.netTotal) || 0;
		const balance = Number(purchaseSummary.balance) || 0;
		let unpaidDeduction = 0;
		let refundTotal = grossReturnTotal;

		if (purchaseNetTotal > 0 && balance > 0 && grossReturnTotal > 0) {
			unpaidDeduction = (grossReturnTotal / purchaseNetTotal) * balance;
			refundTotal = grossReturnTotal - unpaidDeduction;
		}

		setNetTotal(grossReturnTotal);
		setReturnBreakdown({
			grossReturnTotal,
			unpaidDeduction: Number(unpaidDeduction.toFixed(2)),
			refundTotal: Number(refundTotal.toFixed(2)),
		});

		setTaxSummary({
			CGST: Number(CGST.toFixed(2)),
			SGST: Number(SGST.toFixed(2)),
			IGST: Number(IGST.toFixed(2)),
			totalTax: Number(totalTax.toFixed(2)),
			taxableAmount: Number(taxableAmount.toFixed(2)),
		});
	}, [items, purchaseSummary.netTotal, purchaseSummary.balance]);

	useEffect(() => {
		recalculateAll();
	}, [items, recalculateAll]);


	// ============ HELPER FUNCTIONS ============
	const showReturnSubtotal = (item) => {
		if (item.returnQuantity <= 0) return "0.00";
		return (item.returnTotalAmount || 0).toFixed(2);
	};

	const showReturnTotal = (item) => {
		if (item.returnQuantity <= 0) return "0.00";
		return (item.returnTotalAmount || 0).toFixed(2);
	};

	// ============ HANDLE RETURN ============
	const handleReturnStock = async (e) => {
		e.preventDefault();

		const stockItems = items
			.filter(item => item.returnQuantity > 0)
			.map(item => ({
				stockId: item.stockId,
				itemId: item._id,

				batchNumber: item.batchNo || "",
				medicineQuantity: item.returnQuantity,
				packageQuantity: item.returnQuantity,
				itemQuantity: item.returnItemQuantity,

				isOTC: item.isOTC,
				taxableAmount: (item.returnTaxableAmount || 0).toFixed(2),
				// each item’s own values
				tax: (item.returnTaxAmount || 0).toFixed(2),
				taxPercentage: item.taxPercentage,

				isIGST: item.IGSTPercent > 0,
				IGST: item.returnIGSTAmount.toFixed(2),
				SGST: item.returnSGSTAmount.toFixed(2),
				CGST: item.returnCGSTAmount.toFixed(2),

				IGSTPercentage: item.IGSTPercent,
				CGSTPercentage: item.CGSTPercent,
				SGSTPercentage: item.SGSTPercent,

				HSN: item.HSN || "",
				MFG: item.manufacturer || "",

				MRP: item.packageMRP.toFixed(2),
				rate: item.packageRate.toFixed(2)
			}));

		if (stockItems.length === 0) {
			toast.warning(t("Please select quantities to return"));
			return;
		}

		const payload = {
			clinicId: pharmacyPOS?.hospital_id,
			purchaseId: stkId,
			supplierId,
			supplierName,

			stockItems,

			note,
			returnDate: date,

			NetTotal: Number(returnBreakdown.refundTotal).toFixed(2),
			otherNetTotalPrice: Number(returnBreakdown.refundTotal).toFixed(2),
			zigConversionValue: 0,
			currencyType: currency,
			isConversion: false,
			// taxableAmount
			taxableAmount: (taxSummary.taxableAmount || 0).toFixed(2),
			// main-level tax summary
			tax: (taxSummary.totalTax || 0).toFixed(2),
			taxPercentage: purchaseTaxType === "igst"
				? items[0]?.IGSTPercent || 0
				: items[0]?.CGSTPercent + items[0]?.SGSTPercent || 0,

			isIGST,

			// main summary values
			IGST: taxSummary.IGST.toFixed(2),
			CGST: taxSummary.CGST.toFixed(2),
			SGST: taxSummary.SGST.toFixed(2),

			IGSTPercentage: isIGST ? items[0]?.IGSTPercent : 0,
			CGSTPercentage: !isIGST ? items[0]?.CGSTPercent : 0,
			SGSTPercentage: !isIGST ? items[0]?.SGSTPercent : 0
		};

		// console.log(payload);

		try {
			setLoading(true);
			const response = await returnPurchase(payload);
			toast.success(response.data.message || t("Return processed successfully"));
			navigate(-1);
		} catch (error) {
			console.error("Error returning stock:", error);
			toast.warning(error.response?.data?.message || t("Failed to process return"));
		} finally {
			setLoading(false);
		}
	};

	// ============ DETECT TAX TYPE DISPLAY ============
	const showIGST = isIGST || items.some((item) => (item.IGSTPercent || 0) > 0);

	return (
		<Fragment>
			<Breadcrumbs
				mainTitle={t("Return Purchased Items")}
				parent={t("Dashboard")}
				title={t("Return Purchase")}
			/>
			<Col sm="12">
				<Card>
					<CardBody>
						<div className="d-flex justify-content-end tw-w-full mb-3">
							<button className="btn btn-primary" onClick={() => navigate(-1)}>
								<FaArrowLeft className="me-2" /> {t("Back")}
							</button>
						</div>
						<Form onSubmit={handleReturnStock}>
							<div className="row stock-adjustment">
								{/* Supplier */}
								<div className="col-md-6 mb-3">
									<Form.Group>
										<Form.Label className="fw-bold">{t("Supplier")} *</Form.Label>
										<Form.Control
											type="text"
											value={supplierName}
											disabled
										/>
									</Form.Group>
								</div>

								{/* Items Table */}
								{items?.length > 0 && (
									<div className="col-md-12 mb-4">
										<div className="table-responsive">
											<Table bordered size="sm" className="align-middle">
												<thead className="table-light">
													<tr>
														<th>{t("Product")}</th>
														<th>{t("Brand")}</th>
														<th>{t("Batch")}</th>
														<th>{t("Mfg Date")}</th>
														<th>{t("Exp Date")}</th>
														<th className="text-center">{t("Avail Qty")}</th>
														<th className="text-center">{t("Return Qty")}</th>
														<th className="text-center">{t("Unit Cost")}</th>
														{showPurchaseTax && (
															<th className="text-center">{t("Taxable Value")}</th>
														)}

														{/* TAX COLUMNS HEADER - DYNAMIC */}
														{showPurchaseTax && (showIGST ? (
															<>
																<th className="text-center">{t("IGST %")}</th>
																<th className="text-center">{t("IGST Amt")}</th>
															</>
														) : (
															<>
																<th className="text-center">{t("CGST %")}</th>
																<th className="text-center">{t("CGST Amt")}</th>
																<th className="text-center">{t("SGST %")}</th>
																<th className="text-center">{t("SGST Amt")}</th>
															</>
														))}

														{showPurchaseTax && (
															<th className="text-center">{t("Total Tax")}</th>
														)}
														<th className="text-center">{t("GrandTotal")}</th>
														<th className="text-center"></th>
													</tr>
												</thead>
												<tbody>
													{items.map((item) => (
														<tr key={item._id}>
															<td style={{ maxWidth: "100px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
																<OverlayTrigger
																	placement="top"
																	overlay={
																		<Tooltip id={`tooltip-${item._id}`}>
																			{item.brandName}
																		</Tooltip>
																	}
																>
																	<span className="text-truncate d-block">
																		{item.brandName}
																	</span>
																</OverlayTrigger>
															</td>
															<td style={{ maxWidth: "100px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
																<OverlayTrigger
																	placement="top"
																	overlay={
																		<Tooltip id={`tooltip-${item._id}`}>
																			{item.medicineName}
																		</Tooltip>
																	}
																>
																	<span className="text-truncate d-block">
																		{item.medicineName}
																	</span>
																</OverlayTrigger>
															</td>
															<td>{item.batchNo || "-"}</td>
															<td className="text-nowrap">{formatDate(item.manufacturingDate)}</td>
															<td className="text-nowrap">{formatDate(item.expiryDate)}</td>

															<td className="text-center fw-bold">
																{item.itemQuantity}
															</td>
															<td>
																<input
																	type="number"
																	min="0"
																	max={item.itemQuantity}
																	value={item.returnQuantity || ""}
																	className="form-control form-control-sm text-center"
																	onChange={(e) =>
																		updateReturnQuantity(
																			item._id,
																			e.target.value
																		)
																	}
																	{...disableScrollProps}
																/>
															</td>
															<td className="text-end">
																{getCurrencySymbol()}
																{Number(item.unitPrice).toFixed(2)}
															</td>
															{showPurchaseTax && (
																<td className="text-end fw-bold text-primary">
																	{getCurrencySymbol()}
																	{(item.returnTaxableAmount || 0).toFixed(2)}
																</td>
															)}

															{/* TAX DISPLAY - DYNAMIC */}
															{showPurchaseTax && (
																item.IGSTPercent > 0 ? (
																<>
																	<td className="text-center text-muted">
																		{(item.IGSTPercent || 0).toFixed(2)}%
																	</td>
																	<td className="text-end text-success">
																		{getCurrencySymbol()}
																		{(item.returnIGSTAmount || 0).toFixed(2)}
																	</td>
																</>
															) : (
																<>
																	<td className="text-center text-muted">
																		{(item.CGSTPercent || 0).toFixed(2)}%
																	</td>
																	<td className="text-end text-success">
																		{getCurrencySymbol()}
																		{(item.returnCGSTAmount || 0).toFixed(2)}
																	</td>
																	<td className="text-center text-muted">
																		{(item.SGSTPercent || 0).toFixed(2)}%
																	</td>
																	<td className="text-end text-success">
																		{getCurrencySymbol()}
																		{(item.returnSGSTAmount || 0).toFixed(2)}
																	</td>
																</>
															)
															)}

															{showPurchaseTax && (
																<td className="text-end fw-bold text-info">
																	{getCurrencySymbol()}
																	{(item.returnTaxAmount || 0).toFixed(2)}
																</td>
															)}
															<td className="text-end fw-bold text-success">
																{getCurrencySymbol()}
																{/* {showReturnTotal(item)} */}
																{showReturnSubtotal(item)}
															</td>
															<td className="text-center">
																<button
																	type="button"
																	className="btn-danger btn-sm remove-item"
																	onClick={() => removeItem(item._id)}
																>
																	&times;
																</button>
															</td>
														</tr>
													))}

													{/* Totals Row — amounts aligned under Amt / Total Tax / GrandTotal */}
													<tr className="fw-bold bg-light">
														<td colSpan={showPurchaseTax ? 9 : 8} className="text-end">
															{t("Subtotals")}:
														</td>

														{/* TAX TOTALS - DYNAMIC (empty under % cols, values under Amt cols) */}
														{showPurchaseTax && (showIGST ? (
															<>
																<td></td>
																<td className="text-end text-success">
																	{getCurrencySymbol()}
																	{taxSummary.IGST.toFixed(2)}
																</td>
															</>
														) : (
															<>
																<td></td>
																<td className="text-end text-success">
																	{getCurrencySymbol()}
																	{taxSummary.CGST.toFixed(2)}
																</td>
																<td></td>
																<td className="text-end text-success">
																	{getCurrencySymbol()}
																	{taxSummary.SGST.toFixed(2)}
																</td>
															</>
														))}

														{showPurchaseTax && (
															<td className="text-end text-info">
																{getCurrencySymbol()}
																{taxSummary.totalTax.toFixed(2)}
															</td>
														)}
														<td className="text-end text-success">
															{getCurrencySymbol()}
															{netTotal.toFixed(2)}
														</td>
														<td></td>
													</tr>
												</tbody>
											</Table>
										</div>
									</div>
								)}

								{/* Summary Section */}
								{items.length > 0 && (
									<div className="row mt-4">
										{/* payment history */}
										<div className="col-md-6">
											<Table bordered size="sm">
												<tbody>
													{(purchaseSummary.purchaseAmountHistory?.length > 0 ||
														purchaseSummary.totalPaid > 0) && (
															<>
																<tr className="table-light">
																	<td colSpan={2} className="fw-bold">
																		{t("Payment History")}
																	</td>
																</tr>
																{purchaseSummary.purchaseAmountHistory?.length > 0 ? (
																	purchaseSummary.purchaseAmountHistory.map(
																		(payment, idx) => (
																			<tr key={payment._id || idx}>
																				<td className="text-muted">
																					{t("Payment")} {idx + 1} —{" "}
																					{formatDate(payment.date)}
																				</td>
																				<td className="text-end text-success fw-bold">
																					{getCurrencySymbol()}
																					{Number(payment.amount || 0).toFixed(2)}
																				</td>
																			</tr>
																		)
																	)
																) : (
																	<tr>
																		<td className="text-muted">{t("Amount Paid")}</td>
																		<td className="text-end text-success fw-bold">
																			{getCurrencySymbol()}
																			{purchaseSummary.totalPaid.toFixed(2)}
																		</td>
																	</tr>
																)}
																<tr>
																	<td className="text-muted">
																		{t("Original Purchase Total")}
																	</td>
																	<td className="text-end">
																		{getCurrencySymbol()}
																		{purchaseSummary.netTotal.toFixed(2)}
																	</td>
																</tr>
																<tr>
																	<td className="text-muted">{t("Total Paid")}</td>
																	<td className="text-end text-success fw-bold">
																		{getCurrencySymbol()}
																		{purchaseSummary.totalPaid.toFixed(2)}
																	</td>
																</tr>
																<tr>
																	<td className="text-muted">{t("Balance Due")}</td>
																	{/* more bold and big */}
																	<td className="text-end text-warning fw-bold fs-5">
																		{getCurrencySymbol()}
																		{purchaseSummary.balance.toFixed(2)}
																	</td>
																</tr>
															</>
														)}
												</tbody>
											</Table>
										</div>
										{/* Final Totals */}
										<div className="col-md-6 ms-auto">
											<Table bordered size="sm">
												<tbody>
													{showPurchaseTax && (
													<tr>
														<td className="text-end fw-bold">
															{t("Net Taxable Value")}:
														</td>
														<td className="text-end fw-bold">
															{getCurrencySymbol()}
															{taxSummary.taxableAmount?.toFixed(2) || "0.00"}
														</td>
													</tr>
													)}
													{showPurchaseTax && (showIGST ? (
														<tr>
															<td className="text-end fw-bold">
																{t("IGST")}:
															</td>
															<td className="text-end text-success fw-bold">
																{getCurrencySymbol()}
																{taxSummary.IGST.toFixed(2)}
															</td>
														</tr>
													) : (
														<>
															<tr>
																<td className="text-end fw-bold">
																	{t("CGST")}:
																</td>
																<td className="text-end text-success fw-bold">
																	{getCurrencySymbol()}
																	{taxSummary.CGST.toFixed(2)}
																</td>
															</tr>
															<tr>
																<td className="text-end fw-bold">
																	{t("SGST")}:
																</td>
																<td className="text-end text-success fw-bold">
																	{getCurrencySymbol()}
																	{taxSummary.SGST.toFixed(2)}
																</td>
															</tr>
														</>
													))}
													<tr>
														<td className="text-end fw-bold">
															{t("Return Total")}:
														</td>
														<td className="text-end fw-bold">
															{getCurrencySymbol()}
															{returnBreakdown.grossReturnTotal.toFixed(2)}
														</td>
													</tr>
													{isPartialPayment &&
														returnBreakdown.unpaidDeduction > 0 && (
															<tr>
																<td className="text-end text-danger fw-bold">
																	{t("Less: Unpaid Balance")}:
																</td>
																<td className="text-end text-danger fw-bold">
																	- {getCurrencySymbol()}
																	{returnBreakdown.unpaidDeduction.toFixed(2)}
																</td>
															</tr>
														)}
													<tr className="table-success fw-bold">
														<td className="text-end">
															{t("Grand Total Return")}:
														</td>
														<td className="text-end text-success fs-5">
															{getCurrencySymbol()}
															{returnBreakdown.refundTotal.toFixed(2)}
														</td>
													</tr>
												</tbody>
											</Table>
										</div>

										{/* Note & Date */}
										<div className="col-md-12 mt-4">
											<div className="row g-3">
												<div className="col-md-6 ms-auto">
													<Form.Group>
														<Form.Label className="fw-bold">
															{t("Return Date")}
														</Form.Label>
														<Form.Control
															type="date"
															value={date}
															onChange={(e) => setDate(e.target.value)}
															max={new Date().toISOString().split("T")[0]}
														/>
													</Form.Group>
												</div>
											</div>
											<Form.Group className="mt-3">
												<Form.Label className="fw-bold">{t("Note")}</Form.Label>
												<Form.Control
													as="textarea"
													rows={3}
													value={note}
													onChange={(e) => setNote(e.target.value)}
													placeholder={t("Add notes here...")}
												/>
											</Form.Group>
										</div>
									</div>
								)}
							</div>

							{/* Action Buttons */}
							<div className="d-flex justify-content-end gap-2 mt-4">
								<Button
									variant="primary"
									type="submit"
									disabled={
										loading ||
										items.filter((i) => i.returnQuantity > 0).length === 0
									}
								>
									{loading ? t("Processing...") : t("Process Return")}
								</Button>
								<Button
									variant="secondary"
									onClick={() => navigate(-1)}
									disabled={loading}
								>
									{t("Cancel")}
								</Button>
							</div>
						</Form>
					</CardBody>
				</Card>
			</Col>
		</Fragment>
	);
};

export default ReturnPurchase;